import React, { useEffect, useMemo, useRef } from 'react'
import { motion } from 'framer-motion'
import s from './onboarding.module.scss'
import classNames from 'classnames';
import { Button } from '@shared/ui/button/button';
import { useProfile } from '@shared/api/useProfile';
import { useUpdateInstruments } from '@entities/profile/api/useUpdateInstruments';
import { TextWithEmoji } from '@shared/ui/text-with-emoji/text-with-emoji';
import { IconCheck } from '@tabler/icons-react';
import {
	STUDENT_ONBOARDING_PREFERENCE_KEYS,
	getAvailableInstrumentItems,
	resolveAvailableInstrumentByValue,
	resolveInstrumentValueByAvailableInstrument,
	resolveInstrumentValueByProfileInstrument,
} from './lib/student-onboarding-options';
import { itemVariants, optionHover, optionTap, sectionVariants } from '@shared/lib/onboarding-motion';

const {
	instrument: INSTRUMENT_KEY,
	instrumentId: INSTRUMENT_ID_KEY,
	level: LEVEL_KEY,
} = STUDENT_ONBOARDING_PREFERENCE_KEYS;

export const Instrument = React.memo(({setCurrentStep, selectedByQuestion, setSelectedByQuestion, availableInstruments}) => {
	const {data: profile} = useProfile()
	const hasInteractedRef = useRef(false)
	const {mutate: updateProfile, isPending} = useUpdateInstruments({
		onSuccess: () => {
			window.ym(96194074,'reachGoal','save-instruments-by-onboarding')
			setCurrentStep(prev => prev + 1)
		}
	})
	const instruments = useMemo(
		() => availableInstruments || [],
		[availableInstruments]
	)
	const instrumentItems = useMemo(
		() => getAvailableInstrumentItems(instruments),
		[instruments]
	)
	const selectedInstrumentId = selectedByQuestion?.[INSTRUMENT_ID_KEY] || null
	const selectedInstrumentValue = selectedByQuestion?.[INSTRUMENT_KEY] || ''

	useEffect(() => {
		if (hasInteractedRef.current) return;
		if (selectedInstrumentId || selectedInstrumentValue || !profile?.instruments?.[0]) return;

		const profileInstrument = profile.instruments[0];

		setSelectedByQuestion((prev) => ({
			...prev,
			[INSTRUMENT_ID_KEY]: profileInstrument.id,
			[INSTRUMENT_KEY]: resolveInstrumentValueByProfileInstrument(profileInstrument),
		}));
	}, [profile, selectedInstrumentId, selectedInstrumentValue, setSelectedByQuestion])

	useEffect(() => {
		if (selectedInstrumentId || !selectedInstrumentValue || !instruments?.length) return;

		const availableInstrument = resolveAvailableInstrumentByValue(instruments, selectedInstrumentValue);

		if (!availableInstrument?.id) return;

		setSelectedByQuestion((prev) => ({
			...prev,
			[INSTRUMENT_ID_KEY]: availableInstrument.id,
		}));
	}, [instruments, selectedInstrumentId, selectedInstrumentValue, setSelectedByQuestion])

	const clickHandler = (instrument) => {
		hasInteractedRef.current = true
		const nextInstrumentValue = resolveInstrumentValueByAvailableInstrument(instrument)

		setSelectedByQuestion((prev) => {
			const shouldResetLevel = prev?.[INSTRUMENT_KEY] !== nextInstrumentValue;

			return {
				...prev,
				[INSTRUMENT_ID_KEY]: instrument.id,
				[INSTRUMENT_KEY]: nextInstrumentValue,
				...(shouldResetLevel ? { [LEVEL_KEY]: '' } : {}),
			}
		})
	}

	const isInstrumentActive = (instrument) => {
		if (selectedInstrumentId) {
			return String(selectedInstrumentId) === String(instrument?.id);
		}

		return selectedInstrumentValue && resolveInstrumentValueByAvailableInstrument(instrument) === selectedInstrumentValue;
	}

	const onSubmit = () => {
		if (!selectedInstrumentId) return
		updateProfile({instruments: [selectedInstrumentId]})
	}

	return (
		<>
			<motion.div className={s.slideWrapper} variants={sectionVariants} initial="initial" animate="animate">
				<motion.div className={s.titleWrapper} variants={itemVariants}>
					<p className={s.title}>Выбор инструмента</p>
					<p className={s.subtitle}>
						Выбери инструмент, на котором хочешь заниматься. <br />
						Его можно будет изменить в личном кабинете
					</p>
				</motion.div>
				<motion.div className={s.goalOptionsGrid} variants={sectionVariants}>
					{instruments?.length > 0 &&
						instruments?.map((group) =>
							group?.instruments?.map((instrument) => {
								const isActive = isInstrumentActive(instrument)

								return (
									<motion.button
										key={instrument?.id}
										type="button"
										className={classNames(s.goalOptionCard, isActive && s.active)}
										onClick={() => clickHandler(instrument)}
										variants={itemVariants}
										whileHover={optionHover}
										whileTap={optionTap}
									>
										<span className={classNames(s.goalOptionCheck, isActive && s.checked)}>
											{isActive ? <IconCheck size={14} stroke={3} /> : null}
										</span>
										<span className={s.goalOptionLabel}>
											<TextWithEmoji text={instrument?.name}/>
										</span>
									</motion.button>
								)
							})
						)}
				</motion.div>
				<motion.div className={s.btns} variants={itemVariants}>
					<Button
						label='Далее'
						size="small"
						className={s.button}
						disabled={!selectedInstrumentId || instrumentItems.length <= 0 || isPending}
						fetching={isPending}
						onClick={onSubmit}
					/>
				</motion.div>
			</motion.div>
		</>
  );
});
